import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { LinkProps } from 'react-router';
import { Link as RouterLink, matchPath, useLocation, useParams } from 'react-router';
import classNames from 'clsx';
import type { IconButtonProps } from '@mui/material';
import { CloseRounded } from '@mui/icons-material';

import { DESIGN_INLINE_VERTICAL, DESIGN_VERTICAL } from '@/modules/ChannelList/constants/carousels';
import { DEFAULT_VIEW_PATH } from '@/modules/common/constants/routePath';

import { IsInPopupContext } from '../../helpers';
import { throttle } from '../../helpers/events';
import * as commonSelectors from '../../redux/selectors/common';
import { useTranslate } from '@/modules/common/helpers/i18n';
import { isMobileUserAgent } from '@/modules/common/helpers/userAgent';

import { Dialog, IconButton } from '@/mui/components';

import styles from './PagePopup.module.scss';

type PagePopupProps = {
  children: React.ReactNode;
};

function LinkIconButton(props: Omit<IconButtonProps, 'component'> & LinkProps) {
  return <IconButton {...props} component={RouterLink} />;
}

function PagePopup({ children }: PagePopupProps) {
  const open = true;
  const location = useLocation();
  const params = useParams<{ channel: string }>();
  const t = useTranslate();
  const channels = useSelector(commonSelectors.channelsSelector);
  const design = matchPath({ path: DEFAULT_VIEW_PATH }, location.pathname)
    ? channels.find((channel) => channel.id === Number(params.channel))?.design
    : undefined;

  const isVertical = [DESIGN_VERTICAL, DESIGN_INLINE_VERTICAL].includes(design as string);

  const throttledScrollEvent = useMemo(() => throttle(() => window.dispatchEvent(new Event('scroll')), 500), []);

  useEffect(
    () => () => {
      throttledScrollEvent.clear?.();
    },
    [throttledScrollEvent],
  );

  return (
    <Dialog
      classes={{
        container: classNames(styles.Container, {
          [styles.Container___mobile]: isMobileUserAgent,
        }),
        paper: classNames(styles.Paper, {
          [styles.Paper___blurred]: isVertical,
        }),
      }}
      open={open}
      scroll="body"
      onScroll={throttledScrollEvent}
      fullScreen={isVertical}
      fullWidth={isVertical}
      onKeyDown={(event) => {
        if (event.code === 'Escape') {
          // https://github.com/facebook/create-react-app/issues/9485#issuecomment-1356873687
          // Re-dispatch the event to ensure it reaches global listeners
          window.dispatchEvent(
            new KeyboardEvent('keydown', {
              key: event.key,
              code: event.code,
              keyCode: event.keyCode,
              bubbles: true,
              cancelable: true,
              shiftKey: event.shiftKey,
              ctrlKey: event.ctrlKey,
              metaKey: event.metaKey,
              altKey: event.altKey,
            }),
          );
        }
      }}
      disableEscapeKeyDown
    >
      {!isVertical && (
        <LinkIconButton aria-label={t.closePopup.text()} className={styles.ButtonClose} to="/" size="medium">
          <CloseRounded />
        </LinkIconButton>
      )}
      <IsInPopupContext.Provider value>{children}</IsInPopupContext.Provider>
    </Dialog>
  );
}

export default PagePopup;
